import axios from 'axios';
import {AppDispatch} from '@config/store';

import StringUtils from '@/utils/StringUtils';
import {
    GET_EXPENSE_ACCOUNTS_VALIDATION,
    GET_EXPENSE_ACCOUNTS_VALIDATION_ERROR,
    GET_EXPENSE_ACCOUNTS_VALIDATION_SUCCESS,
    IExpenseAccountValidationFilters,
    SET_EXPENSE_ACCOUNTS_VALIDATION_FILTERS,
} from '@components/expenseAccountValidation/redux/types/ExpenseAccountValidationTypes';

const BASE = `${process.env.REACT_APP_BASE_URL}/v2/expense-accounts/validation`;

const authHeaders = (token: string) => ({
    Accept: 'application/json',
    Authorization: 'Bearer ' + token,
});

/**
 * Descarta los filtros vacíos antes de armar el query string.
 *
 * `StringUtils.jsonToQueryString` solo omite la cadena vacía, no `undefined` ni `null`: un
 * filtro sin capturar viajaría como `status=undefined` y el backend lo rechazaría con un
 * 400 porque valida contra el enum. Las fechas se mandan en ISO para que no dependan del
 * formato local del navegador.
 */
const cleanFilters = (filters: IExpenseAccountValidationFilters = {}) =>
    Object.entries(filters).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value === undefined || value === null || value === '') return acc;
        acc[key] = value instanceof Date ? value.toISOString() : String(value);
        return acc;
    }, {});

export const getExpenseAccountsValidation = () => ({
    type: GET_EXPENSE_ACCOUNTS_VALIDATION,
});

export const getExpenseAccountsValidationSuccess = (result: any) => ({
    type: GET_EXPENSE_ACCOUNTS_VALIDATION_SUCCESS,
    value: result,
});

export const getExpenseAccountsValidationError = (error: boolean) => ({
    type: GET_EXPENSE_ACCOUNTS_VALIDATION_ERROR,
    value: error,
});

export const setExpenseAccountsValidationFilters = (filters: IExpenseAccountValidationFilters) => ({
    type: SET_EXPENSE_ACCOUNTS_VALIDATION_FILTERS,
    value: filters,
});

/**
 * Aprueba una Cuenta de Gastos (HU 2, criterio 5).
 *
 * Fuera del store a propósito: es una acción puntual cuyo resultado se refleja recargando
 * el listado, no un estado que otras pantallas necesiten observar. El correo de quien
 * aprueba lo resuelve el backend con el token; aquí no se manda.
 */
export async function approveExpenseAccount(
    token: string,
    customAgentCode: string,
    customRequest: string,
    comments?: string
): Promise<void> {
    await axios.post(
        `${BASE}/${customAgentCode}/custom-request/${encodeURIComponent(customRequest)}/approve`,
        {comments: comments?.trim() || undefined},
        {headers: authHeaders(token)}
    );
}

/**
 * Rechaza una Cuenta de Gastos (HU 2, criterios 8 y 9).
 *
 * `invoiceUuid` es opcional: sin él la nota queda a nivel de toda la Cuenta de Gastos, que
 * es el comportamiento por omisión del criterio 9. El backend lo resuelve a la factura.
 */
export async function rejectExpenseAccount(
    token: string,
    customAgentCode: string,
    customRequest: string,
    payload: {reason: string; comments?: string; invoiceUuid?: string}
): Promise<void> {
    await axios.post(
        `${BASE}/${customAgentCode}/custom-request/${encodeURIComponent(customRequest)}/reject`,
        {
            reason: payload.reason,
            comments: payload.comments?.trim() || undefined,
            invoiceUuid: payload.invoiceUuid || undefined,
        },
        {headers: authHeaders(token)}
    );
}

/** Un renglón del detalle interno (PDPA-5, criterio 2). */
export interface IExpenseAccountDetailRow {
    uuid: string | null;
    /**
     * Clave de importación de SAP (ZI17, ZL10...). Pese al rótulo "Importe CVE" no es un
     * monto: es el código con el que SAP identifica el concepto. Sin ella la factura no
     * se envía, y eso es lo que marca `inconsistency`.
     */
    importKeys: string | null;
    supplierNumbers: string | null;
    supplierName: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    descriptions: string | null;
    /** Subtotal: el importe sin IVA. */
    amounts: string | null;
    taxesIva: string | null;
    ivaAmounts: string | null;
    retentionFactors: string | null;
    retentions: string | null;
    /** Importe total, con IVA. */
    grossAmounts: string | null;
    status: string | null;
    messages: string | null;
    /** Inconsistencia que impide enviarla a SAP (criterio 5). */
    inconsistency: string | null;
    /** Es la factura de honorarios del Agente Aduanal (criterio 4). */
    isFeeInvoice: boolean;
    documents: {id: string; fileName: string; extension: string; sizeBytes: number}[];
}

/** La cabecera de la Cuenta de Gastos consultada (PDPA-5, criterio 1). */
export interface IExpenseAccountDetailHeader {
    folio: string;
    version: number;
    status: string;
    uploadedAt: string;
    rejectionReason: string | null;
}

/** Detalle completo de una Cuenta de Gastos para el usuario interno (PDPA-5). */
export async function fetchExpenseAccountDetail(
    token: string,
    customAgentCode: string,
    customRequest: string,
    page = 1,
    limit = 25
): Promise<{
    rows: IExpenseAccountDetailRow[];
    header?: IExpenseAccountDetailHeader;
    totalPages: number;
    count: number;
}> {
    const {data} = await axios.get(
        `${BASE}/${customAgentCode}/custom-request/${encodeURIComponent(customRequest)}` +
            `?page=${page}&limit=${limit}`,
        {headers: authHeaders(token)}
    );
    return {
        rows: data?.data ?? [],
        header: data?.metadata?.expenseAccount,
        totalPages: data?.metadata?.totalPages ?? 0,
        count: data?.metadata?.count ?? 0,
    };
}

/**
 * Descarga un documento de una factura (PDPA-5, criterio 3).
 *
 * Se identifica por el id que trae el renglón: la ruta en el almacenamiento no viaja al
 * navegador. El nombre sale de la cabecera que arma el servidor, que es el del archivo
 * original.
 */
export async function downloadInvoiceDocument(
    token: string,
    documentId: string,
    fallbackName: string
): Promise<void> {
    const response = await axios.get(`${BASE}/documents/${documentId}`, {
        headers: authHeaders(token),
        responseType: 'blob',
    });
    const disposition: string = response.headers['content-disposition'] ?? '';
    const nameFromHeader = disposition.match(/filename="?([^"]+)"?/)?.[1];
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = nameFromHeader || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Las facturas del expediente, para el selector del modal de rechazo.
 *
 * Sale del detalle porque es donde vive esa información; el listado no la trae y cargarla
 * para las 13 filas solo por si alguien rechaza una sería desperdiciarla.
 */
export async function fetchExpenseAccountInvoices(
    token: string,
    customAgentCode: string,
    customRequest: string
): Promise<{uuid: string; invoiceNumber: string; supplierName: string}[]> {
    const {data} = await axios.get(
        `${BASE}/${customAgentCode}/custom-request/${encodeURIComponent(customRequest)}?limit=200`,
        {headers: authHeaders(token)}
    );
    return (data?.data ?? [])
        .filter((row: any) => row?.uuid)
        .map((row: any) => ({
            uuid: row.uuid,
            invoiceNumber: row.invoiceNumber ?? '',
            supplierName: row.supplierName ?? '',
        }));
}

/** Listado del módulo interno (HU 2, criterios 1, 12 y 13). */
export function fetchGetExpenseAccountsValidation(
    token: string,
    page = 1,
    limit = 10,
    filters: IExpenseAccountValidationFilters = {}
) {
    return async (dispatch: AppDispatch) => {
        const queryString = StringUtils.jsonToQueryString({
            page,
            limit,
            ...cleanFilters(filters),
        });

        dispatch(getExpenseAccountsValidation());
        try {
            const response = await axios.get(`${BASE}${queryString}`, {
                headers: authHeaders(token),
            });
            dispatch(getExpenseAccountsValidationSuccess(response.data));
        } catch (error) {
            dispatch(getExpenseAccountsValidationError(true));
        }
    };
}

/**
 * Descarga el ZIP de la versión vigente (criterio 2).
 *
 * Va con `responseType: 'blob'` y fuera del store: la respuesta es un archivo binario, no
 * estado de la aplicación. El nombre se toma de `Content-Disposition`, que es el que armó
 * el backend con el folio y la versión.
 */
export async function downloadExpenseAccountZip(
    token: string,
    customsAgentCode: string,
    customRequest: string
): Promise<void> {
    const response = await axios.get(
        `${BASE}/${customsAgentCode}/custom-request/${encodeURIComponent(customRequest)}/zip`,
        {headers: authHeaders(token), responseType: 'blob'}
    );

    const disposition: string = response.headers['content-disposition'] ?? '';
    const nameFromHeader = disposition.match(/filename="?([^"]+)"?/)?.[1];
    const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/zip'}));
    const link = document.createElement('a');
    link.href = url;
    link.download = nameFromHeader || `${customRequest}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Sin revocar la URL el blob se queda en memoria mientras viva la pestaña.
    window.URL.revokeObjectURL(url);
}
